import { useCallback, useEffect, useState } from 'react'
import {
  listUserGoals,
  createUserGoal,
  updateUserGoal,
  deleteUserGoal,
  type AdminGoal,
} from '../lib/api'

// Goal Target-Time (Story 1.9, FR31/FR32): admin view + inline CRUD of any
// user's goals, backed by goal_service (caps/last-standard/event-date enforced
// server-side; errors surfaced as readable messages). Replaces the old read
// of the dead profiles.goals blob.

function GoalPill({
  goal,
  busy,
  onSaveDate,
  onDelete,
}: {
  goal: AdminGoal
  busy: boolean
  onSaveDate: (goalId: string, date: string | null) => void
  onDelete: (goalId: string) => void
}) {
  const [date, setDate] = useState(goal.target_date ?? '')
  const dirty = (date || null) !== (goal.target_date ?? null)
  return (
    <div className="flex items-center gap-2 text-sm border-b py-1.5 last:border-b-0">
      <span
        className="inline-block size-2 rounded-full shrink-0"
        style={{ background: goal.goal_type === 'event' ? '#E8B84D' : '#9E9692' }}
        title={goal.goal_type}
      />
      <span className="font-medium flex-1 truncate" title={goal.label}>
        {goal.label}
      </span>
      <span className="text-muted-foreground text-xs">{goal.origin}</span>
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="border rounded px-1.5 py-0.5 text-xs bg-background"
        disabled={busy}
      />
      <button
        className="text-xs px-2 py-0.5 rounded bg-primary text-primary-foreground disabled:opacity-40"
        disabled={busy || !dirty}
        onClick={() => onSaveDate(goal.id, date || null)}
      >
        Save
      </button>
      <button
        className="text-xs px-2 py-0.5 rounded border text-destructive disabled:opacity-40"
        disabled={busy}
        onClick={() => onDelete(goal.id)}
      >
        Remove
      </button>
    </div>
  )
}

export function GoalsPanel({ userId }: { userId: string }) {
  const [goals, setGoals] = useState<AdminGoal[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [adding, setAdding] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [newDate, setNewDate] = useState('')
  const [newType, setNewType] = useState<'standard' | 'event'>('standard')

  const reload = useCallback(async () => {
    try {
      setGoals(await listUserGoals(userId))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load goals')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    setLoading(true)
    void reload()
  }, [reload])

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      setError(null)
      try {
        await fn()
        await reload()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Action failed')
      } finally {
        setBusy(false)
      }
    },
    [reload],
  )

  const handleAdd = () =>
    run(async () => {
      if (!newLabel.trim()) {
        throw new Error('A label is required.')
      }
      await createUserGoal(userId, {
        goal_type: newType,
        label: newLabel.trim(),
        target_date: newType === 'event' ? newDate || null : newDate || null,
      })
      setAdding(false)
      setNewLabel('')
      setNewDate('')
      setNewType('standard')
    })

  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Goals</h3>
        {!adding && (
          <button
            className="text-xs px-2 py-0.5 rounded border disabled:opacity-40"
            disabled={busy}
            onClick={() => setAdding(true)}
          >
            + Add goal
          </button>
        )}
      </div>

      {error && <div className="text-xs text-destructive">{error}</div>}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading goals…</div>
      ) : goals.length === 0 ? (
        <div className="text-sm text-muted-foreground">No goals.</div>
      ) : (
        <div>
          {goals.map((g) => (
            <GoalPill
              key={g.id}
              goal={g}
              busy={busy}
              onSaveDate={(id, date) => run(() => updateUserGoal(userId, id, { target_date: date }))}
              onDelete={(id) => run(() => deleteUserGoal(userId, id))}
            />
          ))}
        </div>
      )}

      {adding && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
          <input
            placeholder="Goal label"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            className="border rounded px-2 py-1 text-sm bg-background flex-1 min-w-[120px]"
            disabled={busy}
          />
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value as 'standard' | 'event')}
            className="border rounded px-1.5 py-1 text-sm bg-background"
            disabled={busy}
          >
            <option value="standard">standard</option>
            <option value="event">event</option>
          </select>
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="border rounded px-1.5 py-1 text-xs bg-background"
            disabled={busy}
          />
          <button
            className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground disabled:opacity-40"
            disabled={busy}
            onClick={handleAdd}
          >
            Create
          </button>
          <button
            className="text-xs px-2 py-1 rounded border"
            disabled={busy}
            onClick={() => {
              setAdding(false)
              setError(null)
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}
